import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, TrendingUp, X, Sliders, ChevronDown, ChevronUp, Minimize2, Maximize2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ParticlesBg from './ParticlesBg';

// Components
import Header from './components/Header';
import LoadingScreen from './components/LoadingScreen';
import SearchBar from './components/SearchBar';
import LanguageToggle from './components/LanguageToggle';
import MobileFiltersButton from './components/MobileFiltersButton';
import AdSenseCard from './components/AdSenseCard';
import WelcomePopup from './components/WelcomePopup';
import PullableFooter from './components/PullableFooter';

// Pages
import FAQPage from './pages/FAQPage';
import PlansPage from './pages/PlansPage';
import CalculatorPage from './pages/CalculatorPage';

// Utils
import { translations } from './utils/translations';
import {
  getInitialLanguage,
  getBrandName,
  sortStorageCapacities,
  formatPrice,
  getStorageColor,
  carrierColors,
  carrierTextColors,
  carrierSortOrder,
  storeColors
} from './utils/helpers';
import { createRandomizedBrandOrder, createPhoneSortComparator } from './utils/phoneSort';

// Config
import { ADSENSE_CONFIG } from './config/adsense.config';

// Styles
import './styles/Base.css';
import './styles/App.css';

const PhonePriceComparator = () => {
  // State management
  const [language, setLanguage] = useState(getInitialLanguage);
  const [phoneData, setPhoneData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [corrections, setCorrections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeProductId, setActiveProductId] = useState(null);
  const [activeButtonRef, setActiveButtonRef] = useState(null);
  const [priceHistoryData, setPriceHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState('home');
  const [filters, setFilters] = useState({
    brands: [],
    phones: [],
    carriers: [],
    stores: [],
    storages: [],
    hasGiftCard: false,
    maxMonthlyPrice: '',
    maxTotalPrice: '',
    maxPriceAfterGC: ''
  });
  
  // State for collapsible cards
  const [collapsedPhones, setCollapsedPhones] = useState(new Set());
  const [collapsedStorages, setCollapsedStorages] = useState(new Set());

  // Get current translations
  const t = translations[language];

  // Toggle language function with localStorage
  const toggleLanguage = () => {
    const newLanguage = language === 'EN' ? 'FR' : 'EN';
    setLanguage(newLanguage);
    localStorage.setItem('mobideal-language', newLanguage);
  };

  // Handle navigation between pages
  const handleNavigation = (page) => {
    setCurrentPage(page);
  };

  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate active filters count for badge
  const activeFiltersCount = useMemo(() => {
    return filters.brands.length + 
           filters.phones.length + 
           filters.carriers.length + 
           filters.stores.length + 
           filters.storages.length + 
           (filters.hasGiftCard ? 1 : 0) +
           (filters.maxMonthlyPrice ? 1 : 0) +
           (filters.maxTotalPrice ? 1 : 0) +
           (filters.maxPriceAfterGC ? 1 : 0);
  }, [filters]);

  const [particleSettings] = useState({
    backgroundColors: 'linear-gradient(135deg, #AD4883 0%, #217858 100%)',
    density: isMobile ? 5 : 30,
    dotColor: '#B9BABB',
    lineColor: '#B9BABB',
    particleRadius: 3,
    lineWidth: 0.7,
    proximity: 150,
    minSpeed: 0.6,
    maxSpeed: 2,
    createOnClick: isMobile ? 0 : 1,
    repulse: isMobile ? 0 : 100,
    zIndex: -1,
  });

  // Load corrections JSON
  useEffect(() => {
    const loadCorrections = async () => {
      try {
        const response = await fetch('/corrections.json');
        const correctionsData = await response.json();
        setCorrections(correctionsData);
      } catch (error) {
        console.error('Error loading corrections:', error);
        setCorrections({}); // Continue with empty corrections if loading fails
      }
    };

    loadCorrections();
  }, []);

  // Apply corrections to data
  const applyCorrections = (data) => {
    if (!corrections.phoneCorrections && !corrections.carrierCorrections && !corrections.storeCorrections && !corrections.globalReplacements) {
      return data; // No corrections to apply
    }

    return data.map(item => {
      let correctedPhone = corrections.phoneCorrections?.[item.Phone] || item.Phone;
      let correctedCarrier = corrections.carrierCorrections?.[item.Carrier] || item.Carrier;
      let correctedStore = corrections.storeCorrections?.[item.Store] || item.Store;

      // Apply global replacements to phone names
      if (corrections.globalReplacements) {
        Object.entries(corrections.globalReplacements).forEach(([search, replace]) => {
          correctedPhone = correctedPhone.replace(new RegExp(search, 'g'), replace);
        });
      }

      return {
        ...item,
        Phone: correctedPhone,
        Carrier: correctedCarrier,
        Store: correctedStore
      };
    });
  };

  // Load and parse API data
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('https://g3a0a684e8149f7-newgate.adb.ca-montreal-1.oraclecloudapps.com/ords/mobideal/product/latest');
        const apiData = await response.json();
        
        let data = apiData.items.map(item => ({
          Phone: item.phone,
          Carrier: item.carrier,
          Storage: item.storage,
          'Price After GC': item['price after gc'],
          'Gift Card': item['gift card'],
          'Total Price': item['total price'],
          'Monthly Price': item['monthly price'],
          'Down Payment': item['down payment'],
          'BIB Monthly': item['bib monthly'],
          'BIB Down': item['bib down'],
          'BIB Premium': item['bib premium'],
          Link: item.link,
          Store: item.store,
          'Last Updated': item.last_updated,
          'Product ID': item['product id']
        }));

        // Apply corrections before setting the data
        data = applyCorrections(data);

        setPhoneData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    // Only load data after corrections are loaded
    if (corrections !== null) {
      loadData();
    }
  }, [corrections]);

  // Handle click on price history button
  const handlePriceHistoryClick = (productId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (activeProductId === productId) {
      setActiveProductId(null);
      setActiveButtonRef(null);
      return;
    }

    setActiveButtonRef(event.currentTarget);
    setActiveProductId(productId);
    fetchPriceHistory(productId);
  };

  // Handle clicking outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isTooltip = event.target.closest('.price-history-tooltip');
      const isPriceHistoryButton = event.target.closest('.price-history-button');
      
      if (!isTooltip && !isPriceHistoryButton) {
        setActiveProductId(null);
        setActiveButtonRef(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Fetch price history data (with caching)
  const fetchPriceHistory = async (productId) => {
    if (priceHistoryData[productId] && !priceHistoryData[productId].error) {
      console.log(`Price history for ${productId} already cached, skipping API call`);
      return;
    }

    if (loadingHistory[productId]) {
      console.log(`Price history for ${productId} already loading`);
      return;
    }

    console.log(`Fetching price history for ${productId}`);
    setLoadingHistory(prev => ({ ...prev, [productId]: true }));

    try {
      const response = await fetch(`https://g3a0a684e8149f7-newgate.adb.ca-montreal-1.oraclecloudapps.com/ords/mobideal/product/history/${productId}`);
      const historyData = await response.json();
      
      const processedData = historyData.items.map(item => {
        const parsed = JSON.parse(item.json_payload);
        return {
          store: parsed.store_name,
          phone: parsed.phone_name,
          storage: parsed.storage,
          carrier: parsed.carrier_name,
          history: parsed.history.map(h => ({
            timestamp: h.run_timestamp,
            date: new Date(h.run_timestamp),
            totalPrice: h.total_price,
            giftCard: h.gift_card
          }))
        };
      });

      const chartData = createChartData(processedData);
      
      setPriceHistoryData(prev => ({
        ...prev,
        [productId]: {
          raw: processedData,
          chart: chartData,
          phone: processedData[0]?.phone || '',
          storage: processedData[0]?.storage || '',
          carrier: processedData[0]?.carrier || '',
          cached: true
        }
      }));

      console.log(`Price history for ${productId} loaded and cached`);
    } catch (error) {
      console.error('Error fetching price history:', error);
      setPriceHistoryData(prev => ({
        ...prev,
        [productId]: {
          error: true,
          message: 'Failed to load price history',
          cached: false
        }
      }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Create chart data from processed history
  const createChartData = (processedData) => {
    if (!processedData.length) return [];

    const groupedByDate = {};

    processedData.forEach(store => {
      store.history.forEach(entry => {
        // Use local date instead of UTC to ensure today's data is included
        const date = new Date(entry.timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;

        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = {};
        }

        if (!groupedByDate[dateKey][store.store]) {
          groupedByDate[dateKey][store.store] = [];
        }

        groupedByDate[dateKey][store.store].push(entry.totalPrice);
      });
    });

    const sortedDates = Object.keys(groupedByDate).sort();

    return sortedDates.map(dateKey => {
      // Parse the date string properly to avoid timezone issues
      const [year, month, day] = dateKey.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed

      const dataPoint = {
        date: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      };

      Object.keys(groupedByDate[dateKey]).forEach(store => {
        const prices = groupedByDate[dateKey][store];
        // Use the lowest price for the day, excluding zeros unless it's the only value
        const nonZeroPrices = prices.filter(price => price > 0);
        dataPoint[store] = nonZeroPrices.length > 0 ? Math.min(...nonZeroPrices) : Math.min(...prices);
      });

      return dataPoint;
    });
  };

  // Calculate Y-axis domain based on data
  const getYAxisDomain = (chartData) => {
    if (!chartData.length) return [0, 1000];
    
    let maxValue = 0;
    chartData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (key !== 'date' && typeof dataPoint[key] === 'number') {
          maxValue = Math.max(maxValue, dataPoint[key]);
        }
      });
    });
    
    if (maxValue <= 1000) {
      return [0, 1000];
    }
    
    return [0, Math.ceil((maxValue + 300) / 100) * 100];
  };

  // Get unique values for filters
  const uniqueValues = useMemo(() => {
    const brandMap = phoneData.reduce((acc, item) => {
      const brand = getBrandName(item.Phone, translations, language);
      
      if (!acc[brand]) acc[brand] = new Set();
      acc[brand].add(item.Phone);
      return acc;
    }, {});

    const brands = Object.keys(brandMap).sort();
    const carriers = [...new Set(phoneData.map(item => item.Carrier))].filter(Boolean)
      .sort((a, b) => {
        const indexA = carrierSortOrder.indexOf(a);
        const indexB = carrierSortOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    const stores = [...new Set(phoneData.map(item => item.Store))].filter(Boolean).sort();
    const storages = sortStorageCapacities([...new Set(phoneData.map(item => item.Storage))]
      .filter(storage => {
        if (!storage) return false;
        const match = storage.match(/(\d+)(GB|TB)/i);
        if (!match) return false;
        const value = parseInt(match[1]);
        const unit = match[2].toUpperCase();
        const valueInGB = unit === 'TB' ? value * 1024 : value;
        return valueInGB >= 32;
      }));
    
    return { 
      brands, 
      carriers, 
      stores,
      storages,
      phonesByBrand: Object.fromEntries(
        Object.entries(brandMap).map(([brand, phones]) => [brand, [...phones].sort()])
      )
    };
  }, [phoneData, language]);

  // Get available phones based on selected brands
  const availablePhones = useMemo(() => {
    if (filters.brands.length === 0) return [];
    
    const phones = new Set();
    filters.brands.forEach(brand => {
      if (uniqueValues.phonesByBrand[brand]) {
        uniqueValues.phonesByBrand[brand].forEach(phone => phones.add(phone));
      }
    });
    return [...phones].sort();
  }, [filters.brands, uniqueValues.phonesByBrand]);

  // Generate randomized brand order (stable per session)
  const randomizedBrandOrder = useMemo(() => {
    return createRandomizedBrandOrder();
  }, []);

  // Custom phone sorting function
  const sortPhones = useMemo(() => {
    return createPhoneSortComparator(
      randomizedBrandOrder,
      (phone) => getBrandName(phone, translations, language)
    );
  }, [language, randomizedBrandOrder]);

  // Filter and group data
  const filteredAndGroupedData = useMemo(() => {
    let filtered = phoneData.filter(item => {
      // Search filter - tokenize and check if all search terms are present
      if (searchQuery.trim()) {
        const searchTokens = searchQuery.toLowerCase().trim().split(/\s+/);
        const phoneName = item.Phone.toLowerCase();
        const allTokensFound = searchTokens.every(token => phoneName.includes(token));
        if (!allTokensFound) {
          return false;
        }
      }
      
      // Brand filter
      if (filters.brands.length > 0) {
        const itemBrand = getBrandName(item.Phone, translations, language);
        if (!filters.brands.includes(itemBrand)) return false;
      }

      // Phone filter
      if (filters.phones.length > 0 && !filters.phones.includes(item.Phone)) return false;

      // Carrier filter
      if (filters.carriers.length > 0 && !filters.carriers.includes(item.Carrier)) return false;

      // Storage filter
      if (filters.storages.length > 0 && !filters.storages.includes(item.Storage)) return false;

      // Gift Card filter
      if (filters.hasGiftCard && (!item['Gift Card'] || item['Gift Card'] <= 0)) return false;

      // Price filters
      if (filters.maxMonthlyPrice && item['Monthly Price'] > parseFloat(filters.maxMonthlyPrice)) return false;
      if (filters.maxTotalPrice && item['Total Price'] > parseFloat(filters.maxTotalPrice)) return false;
      if (filters.maxPriceAfterGC && item['Price After GC'] > parseFloat(filters.maxPriceAfterGC)) return false;

      return true;
    });

    // If store filter is applied, we need to ensure we only show phones that have data for those stores
    if (filters.stores.length > 0) {
      const phonesWithSelectedStores = new Set();
      phoneData.forEach(item => {
        if (filters.stores.includes(item.Store)) {
          phonesWithSelectedStores.add(item.Phone + '|' + item.Storage + '|' + item.Carrier);
        }
      });

      filtered = filtered.filter(item => {
        return phonesWithSelectedStores.has(item.Phone + '|' + item.Storage + '|' + item.Carrier);
      });
    }

    // Group by phone model, then by storage
    const grouped = filtered.reduce((acc, item) => {
      if (!acc[item.Phone]) {
        acc[item.Phone] = {};
      }
      if (!acc[item.Phone][item.Storage]) {
        acc[item.Phone][item.Storage] = {};
      }
      if (!acc[item.Phone][item.Storage][item.Store]) {
        acc[item.Phone][item.Storage][item.Store] = [];
      }
      acc[item.Phone][item.Storage][item.Store].push(item);
      return acc;
    }, {});

    // Sort storage within each phone
    const sortedGrouped = {};
    Object.keys(grouped).forEach(phone => {
      sortedGrouped[phone] = {};
      const sortedStorages = sortStorageCapacities(Object.keys(grouped[phone]));
      sortedStorages.forEach(storage => {
        sortedGrouped[phone][storage] = grouped[phone][storage];
      });
    });

    return sortedGrouped;
  }, [phoneData, filters, searchQuery, language]);

  const handleMultiSelect = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const removeFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].filter(item => item !== value)
    }));
  };

  // Toggle phone card collapse
  const togglePhoneCollapse = (phone) => {
    setCollapsedPhones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phone)) {
        newSet.delete(phone);
      } else {
        newSet.add(phone);
      }
      return newSet;
    });
  };

  // Toggle storage section collapse
  const toggleStorageCollapse = (phoneStorage) => {
    setCollapsedStorages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phoneStorage)) {
        newSet.delete(phoneStorage);
      } else {
        newSet.add(phoneStorage);
      }
      return newSet;
    });
  };

  // Initialize collapsed storages - collapse all except first storage option for each phone
  useEffect(() => {
    const storageKeysToCollapse = new Set();
    Object.entries(filteredAndGroupedData).forEach(([phone, storageData]) => {
      const storageOptions = Object.keys(storageData);
      // Collapse all storage options except the first one (index 0)
      storageOptions.forEach((storage, index) => {
        if (index > 0) {
          storageKeysToCollapse.add(`${phone}-${storage}`);
        }
      });
    });
    setCollapsedStorages(storageKeysToCollapse);
  }, [filteredAndGroupedData]);

  // Calculate tooltip position dynamically
  const getTooltipPosition = () => {
    if (!activeButtonRef) return { left: 0, top: 0 };
    
    if (isMobile) {
      return {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }
    
    const rect = activeButtonRef.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 500;
    const tooltipHeight = 400;
    
    let left = rect.left;
    let top = rect.bottom + 8;
    
    if (left + tooltipWidth > viewportWidth) {
      left = viewportWidth - tooltipWidth - 16;
    }
    if (left < 16) {
      left = 16;
    }
    
    if (top + tooltipHeight > viewportHeight) {
      top = rect.top - tooltipHeight - 8;
    }
    if (top < 16) {
      top = 16;
    }
    
    return { left, top };
  };

  // Price History Tooltip Component
  const PriceHistoryTooltip = ({ productId }) => {
    if (!activeProductId || activeProductId !== productId || !activeButtonRef) return null;

    const historyData = priceHistoryData[productId];
    const isLoading = loadingHistory[productId];
    const position = getTooltipPosition();

    return (
      <>
        {isMobile && (
          <div className="price-history-mobile-backdrop" onClick={() => {
            setActiveProductId(null);
            setActiveButtonRef(null);
          }} />
        )}
        
        <div 
          className={`price-history-tooltip ${isMobile ? 'mobile' : ''}`}
          style={position}
        >
          <div className="price-history-tooltip-header">
            <TrendingUp size={16} className="price-history-tooltip-icon" />
            {t.priceHistoryTitle}
            {isMobile && (
              <button 
                className="price-history-close-btn"
                onClick={() => {
                  setActiveProductId(null);
                  setActiveButtonRef(null);
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="price-history-loading">
              {t.loadingPriceHistory}
            </div>
          ) : historyData ? (
            <div>
              <div className="price-history-product-info">
                {historyData.phone} - {historyData.storage} ({historyData.carrier})
              </div>
              
              <div className="price-history-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyData.chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      fontSize={isMobile ? 8 : 10}
                      tick={{ fill: '#6B7280' }}
                    />
                    <YAxis 
                      fontSize={isMobile ? 8 : 10}
                      tick={{ fill: '#6B7280' }}
                      domain={getYAxisDomain(historyData.chart)}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value?.toFixed(2)}`, name]}
                      labelStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      contentStyle={{
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        fontSize: isMobile ? '10px' : '12px'
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: isMobile ? '9px' : '11px' }}
                    />
                    {historyData.raw.map((store, index) => (
                      <Line
                        key={store.store}
                        type="monotone"
                        dataKey={store.store}
                        stroke={storeColors[store.store] || storeColors.default}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5, stroke: storeColors[store.store] || storeColors.default }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="price-history-product-id">
                {t.productId} {productId}
              </div>
            </div>
          ) : (
            <div className="price-history-fallback">
              <div className="price-history-fallback-message">
                ⏳ {t.loadingPriceHistory}
              </div>
              <div className="price-history-fallback-id">
                {t.productId} {productId}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // Loading UI
  if (loading) {
    return <LoadingScreen translations={translations} language={language} />;
  }

  return (
    <div className="app-container" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Welcome Popup */}
      <WelcomePopup
        language={language}
        translations={translations}
        toggleLanguage={toggleLanguage}
      />

      {currentPage === 'home' && (
        <MobileFiltersButton
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          activeFiltersCount={activeFiltersCount}
          isMobile={isMobile}
        />
      )}

      <LanguageToggle
        language={language}
        toggleLanguage={toggleLanguage}
      />

      {/* Floating Filters Toggle Button - Desktop Only */}
      {!isMobile && currentPage === 'home' && (
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`filters-toggle-button ${filtersOpen ? 'active' : ''}`}
        >
          <Sliders className="filters-toggle-icon" />
          {t.filters}
          {activeFiltersCount > 0 && (
            <div className="filters-badge">{activeFiltersCount}</div>
          )}
        </button>
      )}

      {/* Filters Panel Overlay */}
      <div 
        className={`filters-overlay ${filtersOpen ? 'active' : ''}`}
        onClick={() => setFiltersOpen(false)}
      />

      {/* Floating Filters Panel */}
      <div className={`filters-panel ${filtersOpen ? 'active' : ''}`}>
        <div className="filters-panel-header">
          <div className="filters-panel-title">
            <Filter size={24} />
            <h2>{t.filters}</h2>
          </div>
          <p className="filters-panel-subtitle">{t.filtersSubtitle}</p>
          <button
            onClick={() => setFiltersOpen(false)}
            className="filters-close-button"
          >
            <X className="filters-close-icon" />
          </button>
        </div>

        <div className="filters-content">
          <div className="filters-grid">
            {/* Brand Filter */}
            <div className="filter-group">
              <label className="filter-label">{t.brands}</label>
              <div className="filter-options">
                {uniqueValues.brands.map(brand => (
                  <label key={brand} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.brands.includes(brand)}
                      onChange={() => handleMultiSelect('brands', brand)}
                      className="filter-checkbox"
                    />
                    {brand}
                  </label>
                ))}
              </div>
            </div>

            {/* Phone Model Filter */}
            <div className="filter-group">
              <label className="filter-label">{t.phoneModels}</label>
              <div className="filter-options">
                {availablePhones.length > 0 ? (
                  availablePhones.map(phone => (
                    <label key={phone} className="filter-option">
                      <input
                        type="checkbox"
                        checked={filters.phones.includes(phone)}
                        onChange={() => handleMultiSelect('phones', phone)}
                        className="filter-checkbox"
                      />
                      {phone}
                    </label>
                  ))
                ) : (
                  <p className="filter-placeholder">{t.selectBrandsFirst}</p>
                )}
              </div>
            </div>

            {/* Carrier Filter */}
            <div className="filter-group">
              <label className="filter-label">{t.carriers}</label>
              <div className="filter-options">
                {uniqueValues.carriers.map(carrier => (
                  <label key={carrier} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.carriers.includes(carrier)}
                      onChange={() => handleMultiSelect('carriers', carrier)}
                      className="filter-checkbox"
                    />
                    {carrier}
                  </label>
                ))}
              </div>
            </div>

            {/* Store Filter */}
            <div className="filter-group">
              <label className="filter-label">{t.stores}</label>
              <div className="filter-options">
                {uniqueValues.stores.map(store => (
                  <label key={store} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.stores.includes(store)}
                      onChange={() => handleMultiSelect('stores', store)}
                      className="filter-checkbox"
                    />
                    {store}
                  </label>
                ))}
              </div>
            </div>

            {/* Storage Filter */}
            <div className="filter-group">
              <label className="filter-label">{t.storage}</label>
              <div className="filter-options">
                {uniqueValues.storages.map(storage => (
                  <label key={storage} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.storages.includes(storage)}
                      onChange={() => handleMultiSelect('storages', storage)}
                      className="filter-checkbox"
                    />
                    {storage}
                  </label>
                ))}
              </div>
            </div>

            {/* Price Filters */}
            <div className="filter-group2">
              <div className="filter-subgroup">
                <label className="filter-label">{t.maxMonthlyPrice}</label>
                <input
                  type="number"
                  placeholder={t.any}
                  value={filters.maxMonthlyPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxMonthlyPrice: e.target.value }))}
                  className="filter-input"
                />
              </div>
              <div className="filter-subgroup">
                <label className="filter-label">{t.maxTotalPrice}</label>
                <input
                  type="number"
                  placeholder={t.any}
                  value={filters.maxTotalPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxTotalPrice: e.target.value }))}
                  className="filter-input"
                />
              </div>
            </div>

            <div className="filter-group2">
              <div className="filter-subgroup">
                <label className="filter-label">{t.maxPriceAfterGC}</label>
                <input
                  type="number"
                  placeholder={t.any}
                  value={filters.maxPriceAfterGC}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxPriceAfterGC: e.target.value }))}
                  className="filter-input"
                />
              </div>
              <div className="filter-subgroup">
                <label className="filter-label">{t.giftCard}</label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={filters.hasGiftCard}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasGiftCard: e.target.checked }))}
                    className="filter-checkbox"
                  />
                  {t.giftCardPromotion}
                </label>
              </div>
            </div>
          </div>

          {/* Active Filters in Panel */}
          {activeFiltersCount > 0 && (
            <div className="filters-panel-active-filters">
              <h3>{t.activeFilters}</h3>
              <div className="active-filters">
                {[...filters.brands, ...filters.phones, ...filters.carriers, ...filters.stores, ...filters.storages].map((filter, index) => (
                  <span key={index} className="active-filter-tag">
                    {filter}
                    <button
                      onClick={() => {
                        if (filters.brands.includes(filter)) removeFilter('brands', filter);
                        else if (filters.phones.includes(filter)) removeFilter('phones', filter);
                        else if (filters.carriers.includes(filter)) removeFilter('carriers', filter);
                        else if (filters.stores.includes(filter)) removeFilter('stores', filter);
                        else if (filters.storages.includes(filter)) removeFilter('storages', filter);
                      }}
                      className="remove-filter-btn"
                    >
                      <X className="remove-icon" />
                    </button>
                  </span>
                ))}
                {filters.hasGiftCard && (
                  <span className="active-filter-tag">
                    {t.giftCardPromotion}
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, hasGiftCard: false }))}
                      className="remove-filter-btn"
                    >
                      <X className="remove-icon" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Particle Background */}
      <ParticlesBg {...particleSettings} />

      {/* Foreground Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header
          translations={translations}
          language={language}
          currentPage={currentPage}
          onNavigate={handleNavigation}
        />

        {/* Conditional Page Rendering */}
        {currentPage === 'faq' ? (
          <FAQPage translations={translations} language={language} />
        ) : currentPage === 'plans' ? (
          <PlansPage translations={translations} language={language} />
        ) : currentPage === 'calculator' ? (
          <CalculatorPage translations={translations} language={language} />
        ) : currentPage === 'chatbot' ? (
          <div className="main-content">
            <h1 style={{ textAlign: 'center', padding: '100px 20px' }}>Chatbot page coming soon...</h1>
          </div>
        ) : (
          /* Main Content - Home Page */
          <div className="main-content">
          <SearchBar 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            translations={translations}
            language={language}
          />

          {/* Results */}
          <div className="results-container">
            {Object.keys(filteredAndGroupedData).length === 0 ? (
              <div className="no-results">
                <Search className="no-results-icon" />
                <p>{t.noResultsMessage}</p>
              </div>
            ) : (
              Object.entries(filteredAndGroupedData).sort(([a], [b]) => sortPhones(a, b)).map(([phone, storageData], index) => {
                const isPhoneCollapsed = collapsedPhones.has(phone);
                return (
                  <React.Fragment key={phone}>
                    {/* Insert ad card every N phones (configured in adsense.config.js) */}
                    {index > 0 && index % ADSENSE_CONFIG.AD_FREQUENCY === 0 && (
                      <AdSenseCard
                        index={index}
                        showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                        adClient={ADSENSE_CONFIG.CLIENT_ID}
                        adSlot={ADSENSE_CONFIG.AD_SLOT}
                      />
                    )}
                    <div className={`phone-card ${isPhoneCollapsed ? 'collapsed' : ''}`}>
                    <div className="phone-header" onClick={() => togglePhoneCollapse(phone)}>
                      <h2>{phone}</h2>
                      <button className="collapse-button">
                        {isPhoneCollapsed ? (
                          <Maximize2 size={20} />
                        ) : (
                          <Minimize2 size={20} />
                        )}
                      </button>
                    </div>
                    
                    {!isPhoneCollapsed && (
                      <div className="phone-content">
                    {Object.entries(storageData).map(([storage, storeData]) => {
                      const storageStyle = getStorageColor(storage);
                      const storageKey = `${phone}-${storage}`;
                      const isStorageCollapsed = collapsedStorages.has(storageKey);
                      return (
                        <div key={storage} className={`storage-container ${isStorageCollapsed ? 'collapsed' : ''}`} style={{
                          backgroundColor: storageStyle.bg,
                          borderColor: storageStyle.border
                        }}>
                          <div className="storage-layout">
                            {/* Storage Ribbon */}
                            <div 
                              className="storage-ribbon clickable" 
                              style={{
                                backgroundColor: storageStyle.border,
                                color: storageStyle.text
                              }}
                              onClick={() => toggleStorageCollapse(storageKey)}
                            >
                              <div className="storage-text">{storage}</div>
                              <button className="storage-collapse-button">
                                {isStorageCollapsed ? (
                                  <ChevronDown size={16} />
                                ) : (
                                  <ChevronUp size={16} />
                                )}
                              </button>
                            </div>
                            
                            {/* Carrier Content */}
                            {!isStorageCollapsed && (
                              <div className="carrier-content">
                              {(() => {
                                const carrierData = {};
                                Object.entries(storeData).forEach(([store, offers]) => {
                                  offers.forEach(offer => {
                                    if (!carrierData[offer.Carrier]) {
                                      carrierData[offer.Carrier] = {};
                                    }
                                    carrierData[offer.Carrier][store] = offer;
                                  });
                                });

                                return Object.entries(carrierData)
                                  .sort(([a], [b]) => {
                                    const indexA = carrierSortOrder.indexOf(a);
                                    const indexB = carrierSortOrder.indexOf(b);
                                    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                                    if (indexA === -1) return 1;
                                    if (indexB === -1) return -1;
                                    return indexA - indexB;
                                  })
                                  .map(([carrier, stores]) => (
                                    <div key={carrier} className="carrier-card">
                                      <div className="carrier-layout">
                                        {/* Carrier Ribbon */}
                                        <div 
                                          className="carrier-ribbon"
                                          style={{
                                            backgroundColor: carrierColors[carrier] || carrierColors.default,
                                            color: carrierTextColors[carrier] || carrierTextColors.default,
                                            borderColor: carrier === 'Virgin Plus' ? '#E5E7EB' : 'transparent'
                                          }}
                                        >
                                          <div className="carrier-text">{carrier}</div>
                                        </div>
                                        
                                        {/* Table Content */}
                                        <div className="table-container">
                                          <table className="comparison-table">
                                            <thead>
                                              <tr>
                                                <th>{t.store}</th>
                                                <th>{t.promotions}</th>
                                                <th>{t.total}</th>
                                                <th>{t.monthly}</th>
                                                <th>{t.down}</th>
                                                <th className="bib-column">{t.bringItBack}</th>
                                                <th style={{ display: 'none' }}>{t.priceTrend}</th>
                                                <th className="actions-column">{t.actions}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {uniqueValues.stores.map((store, index) => {
                                                const offer = stores[store];
                                                const isAvailable = offer !== undefined;
                                                
                                                return (
                                                  <tr key={store} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                                                    <td className="store-cell">
                                                      <span className={isAvailable ? 'available' : 'unavailable'}>
                                                        {store}
                                                      </span>
                                                      {!isAvailable && <span className="unavailable-text">{t.unavailable}</span>}
                                                    </td>
                                                    
                                                    <td className="promotions-cell">
                                                      <div className={isAvailable ? 'promotion-available' : 'promotion-unavailable'}>
                                                        {isAvailable && offer['Gift Card'] > 0 ? (
                                                          <div>
                                                            <div className="promotion-main">{t.giftCardLabel} {formatPrice(offer['Gift Card'])}</div>
                                                            <div className="promotion-secondary">{t.totalAfterGC} {formatPrice(offer['Price After GC'])}</div>
                                                          </div>
                                                        ) : (isAvailable ? '—' : '—')}
                                                      </div>
                                                    </td>
                                                    
                                                    <td className="price-cell">
                                                      <span className={isAvailable ? 'price-available' : 'price-unavailable'}>
                                                        {isAvailable ? formatPrice(offer['Total Price']) : '—'}
                                                      </span>
                                                    </td>
                                                    
                                                    <td className="price-cell">
                                                      <span className={isAvailable ? 'price-monthly' : 'price-unavailable'}>
                                                        {isAvailable ? formatPrice(offer['Monthly Price']) : '—'}
                                                      </span>
                                                    </td>
                                                    
                                                    <td className="price-cell">
                                                      <span className={isAvailable && offer['Down Payment'] > 0 ? 'price-down' : 'price-unavailable'}>
                                                        {isAvailable ? (offer['Down Payment'] > 0 ? formatPrice(offer['Down Payment']) : '—') : '—'}
                                                      </span>
                                                    </td>
                                                    
                                                    <td className="bib-cell bib-column">
                                                      <div className={isAvailable && offer['BIB Premium'] && offer['BIB Premium'] !== 'N/A' && offer['BIB Premium'] !== null && offer['BIB Premium'] > 0 ? 'bib-available' : 'bib-unavailable'}>
                                                        {isAvailable && offer['BIB Premium'] && offer['BIB Premium'] !== 'N/A' && offer['BIB Premium'] !== null && offer['BIB Premium'] > 0 ? (
                                                          <div>
                                                            <div className="bib-main">{t.premium} {formatPrice(offer['BIB Premium'])}</div>
                                                            {offer['BIB Monthly'] && offer['BIB Monthly'] !== 'N/A' && offer['BIB Monthly'] !== null && offer['BIB Monthly'] > 0 ? (
                                                              <div className="bib-secondary">{t.monthly}: {formatPrice(offer['BIB Monthly'])}</div>
                                                            ) : null}
                                                            {offer['BIB Down'] && offer['BIB Down'] !== 'N/A' && offer['BIB Down'] !== null && offer['BIB Down'] > 0 ? (
                                                              <div className="bib-secondary">{t.down}: {formatPrice(offer['BIB Down'])}</div>
                                                            ) : null}
                                                          </div>
                                                        ) : '—'}
                                                      </div>
                                                    </td>
                                                    
                                                    <td className="trend-cell" style={{ display: 'none' }}>
                                                      <span className="trend-unavailable">—</span>
                                                    </td>
                                                    
                                                    <td className="action-cell actions-column">
                                                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                        {isAvailable && offer.Link ? (
                                                          <a
                                                            href={offer.Link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="link-button"
                                                          >
                                                            {t.link}
                                                          </a>
                                                        ) : (
                                                          <span className="link-unavailable">—</span>
                                                        )}
                                                        
                                                        {isAvailable && offer['Product ID'] ? (
                                                          <button
                                                            className="price-history-button"
                                                            onClick={(e) => handlePriceHistoryClick(offer['Product ID'], e)}
                                                          >
                                                            {t.priceHistory}
                                                          </button>
                                                        ) : (
                                                          <span className="link-unavailable">—</span>
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  ));
                              })()}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                      </div>
                    )}
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
        )}
      </div>

      {/* Price History Tooltip */}
      {activeProductId && (
        <PriceHistoryTooltip productId={activeProductId} />
      )}

      {/* Pullable Footer */}
      <PullableFooter onNavigate={handleNavigation} />
    </div>
  );
};

export default PhonePriceComparator;